import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, CheckCircle } from 'lucide-react';
import api from '../api/axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      toast.error('Token inválido');
      navigate('/login');
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('¡Contraseña actualizada correctamente!');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al actualizar contraseña';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="reset-password-page">
      <Card className="max-w-md w-full glass-effect border-white/10">
        <CardHeader>
          <div className="mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_virtualgolf-2/artifacts/si8l522g_IMG_1926.png" 
              alt="City Virtual Golf" 
              className="h-16 w-auto mx-auto mb-4"
            />
          </div>
          <CardTitle className="text-3xl font-heading font-bold uppercase tracking-tight text-center" data-testid="reset-password-title">
            Nueva Contraseña
          </CardTitle>
          <CardDescription className="text-center mt-2" data-testid="reset-password-subtitle">
            {success ? '¡Contraseña actualizada!' : 'Ingresa tu nueva contraseña'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {success ? (
            <div className="space-y-4">
              <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg text-center" data-testid="success-message">
                <CheckCircle className="h-12 w-12 text-primary mx-auto mb-3" />
                <p className="font-semibold mb-2">¡Contraseña actualizada correctamente!</p>
                <p className="text-sm text-muted-foreground">
                  Redirigiendo al inicio de sesión...
                </p>
              </div>

              <Link 
                to="/login" 
                className="block text-center text-accent hover:underline"
                data-testid="go-to-login-link"
              >
                Ir al inicio de sesión
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
              <div>
                <Label htmlFor="password">Nueva Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                  className="mt-1 bg-white/5 border-white/10"
                  data-testid="reset-password-input"
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite tu contraseña"
                  required
                  minLength={6}
                  className="mt-1 bg-white/5 border-white/10"
                  data-testid="reset-password-confirm-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider"
                disabled={loading}
                data-testid="reset-password-submit-button"
              >
                {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};